package fyi.moko.hotpl.data.network

import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.domain.model.Visibility
import fyi.moko.hotpl.domain.model.SyncState
import fyi.moko.hotpl.domain.model.RevisitCadence
import fyi.moko.hotpl.domain.model.CostFeel
import fyi.moko.hotpl.domain.model.Visit
import fyi.moko.hotpl.domain.model.sharedPhotoFileNames
import kotlinx.serialization.Serializable

@Serializable
data class RemotePlaceDto(
    val id: String,
    val ownerId: String,
    val name: String,
    val note: String?,
    val locationContext: String?,
    val tags: List<String>,
    val liked: Boolean,
    val latitude: Double?,
    val longitude: Double?,
    @Serializable(with = FirestoreLong::class)
    val createdAt: Long,
    @Serializable(with = FirestoreLong::class)
    val updatedAt: Long,
    val imageFileName: String?,
    /**
     * 사진 목록. 구버전 클라이언트가 쓴 문서에는 이 필드가 없으므로 기본값이 필요하다 —
     * 그때는 [imageFileName] 한 장만 있는 목록으로 되살린다(toDomain 참고).
     */
    val photos: List<PhotoRefDto> = emptyList(),
    val status: String,
    val visibility: String,
    @Serializable(with = FirestoreLong::class)
    val version: Long,
    val isDeleted: Boolean,
    val revisitCadence: String? = null,
    val costFeel: String? = null,
    val phoneNumber: String? = null,
    val website: String? = null,
)

@Serializable
data class PublicPlaceDto(
    val placeId: String,
    val ownerId: String,
    val name: String,
    val status: String,
    val liked: Boolean,
    val latitude: Double?,
    val longitude: Double?,
    val tags: List<String>,
    @Serializable(with = FirestoreLong::class)
    val updatedAt: Long,
    val visibility: String,
    /**
     * Storage object names under `shared/{ownerId}/` a friend may fetch (backlog 0-36).
     * Defaulted so a document written before this field decodes as "no photos".
     */
    val photos: List<String> = emptyList(),
    /** Business contact from a Directory import — not personal data, so it travels. */
    val phoneNumber: String? = null,
    val website: String? = null,
)

// Mapping functions between Place domain model and remote DTOs
fun Place.toRemoteDto(): RemotePlaceDto = RemotePlaceDto(
    id = id,
    ownerId = ownerId,
    name = name,
    note = note,
    locationContext = locationContext,
    tags = tags,
    liked = liked,
    latitude = latitude,
    longitude = longitude,
    createdAt = createdAt,
    updatedAt = updatedAt,
    imageFileName = imageFileName,
    photos = photos.map { PhotoRefDto(it.fileName, it.kind?.name) },
    status = status.name,
    visibility = visibility.name,
    version = version,
    isDeleted = isDeleted,
    revisitCadence = revisitCadence?.name,
    costFeel = costFeel?.name,
    phoneNumber = phoneNumber,
    website = website,
)

fun RemotePlaceDto.toDomain(): Place = Place(
    id = id,
    name = name,
    note = note,
    locationContext = locationContext,
    tags = tags,
    liked = liked,
    latitude = latitude,
    longitude = longitude,
    createdAt = createdAt,
    updatedAt = updatedAt,
    imageFileName = imageFileName,
    photos = photos.toDomainPhotos(imageFileName),
    status = try { PlaceStatus.valueOf(status) } catch (e: Exception) { PlaceStatus.VISITED },
    visibility = try { Visibility.valueOf(visibility) } catch (e: Exception) { Visibility.PRIVATE },
    syncState = SyncState.SYNCED,
    ownerId = ownerId,
    version = version,
    isDeleted = isDeleted,
    revisitCadence = revisitCadence?.let { runCatching { RevisitCadence.valueOf(it) }.getOrNull() },
    costFeel = costFeel?.let { runCatching { CostFeel.valueOf(it) }.getOrNull() },
    phoneNumber = phoneNumber,
    website = website,
)

/** [visits]: the place's live visits, for the food photos the projection shares (slice B). */
fun Place.toPublicDto(visits: List<Visit> = emptyList()): PublicPlaceDto = PublicPlaceDto(
    placeId = id,
    ownerId = ownerId,
    name = name,
    status = status.name,
    liked = liked,
    latitude = latitude,
    longitude = longitude,
    tags = tags,
    updatedAt = updatedAt,
    visibility = visibility.name,
    photos = sharedPhotoFileNames(visits),
    phoneNumber = phoneNumber,
    website = website,
)
